import type { WebPageData } from '@fullstory/client-api';
import type { MobilePageData } from '@fullstory/playback-mobile';
import type { ServerPageData } from '@fullstory/playback-server';

// Shared utility types distinguishing mobile, web, and server pages
// based on PageDesc.type. Used by other packages but not
// currently used in this package.

// pagePlatformByType maps page "type", the string value used by the
// server to categorize recorded pages, to "platform", the data model
// category used by the playback renderer. This distinction isn't upheld
// consistently, and elsewhere, "platform" is also called "type".
export const pagePlatformByType = {
  '': 'web', // empty-string is implicitly a web loader
  web: 'web',
  android: 'mobile',
  ios: 'mobile',
  mobilemock: 'mobile',
  server: 'server',
} as const;

// Derive various types from pagePlatformByType.
type PagePlatformByType = typeof pagePlatformByType;
export type PagePlatform = PagePlatformByType[keyof PagePlatformByType];
export type PagePlatformForType<T extends PageDescType> = PagePlatformByType[T];

export type PageDescType = keyof PagePlatformByType;
export type PageTypesOfPlatform<T extends PagePlatform> =
  PagePlatformForType<PageDescType> extends T ? PageDescType : never;

export type WebPageDescType = PageTypesOfPlatform<'web'>;
export type MobilePageDescType = PageTypesOfPlatform<'mobile'>;
export type ServerPageDescType = PageTypesOfPlatform<'server'>;

export type PageDataForPlatform<P extends PagePlatform> = P extends 'web'
  ? WebPageData
  : P extends 'mobile'
  ? MobilePageData
  : P extends 'server'
  ? ServerPageData
  : never;

export type PageDataOfType<T extends PageDescType> = PageDataForPlatform<PagePlatformByType[T]>;
